import { AutomationsEmptyState } from './AutomationsEmptyState';
import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { SingleAutomationDialog, SingleAutomationDialogRef } from './SingleAutomationDialog';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import {
  AutomationSortOption,
  getAddAutomationButtonByType,
  getDedupMatchingDefaultAutomation,
} from './helper';
import { Button } from '@sweep-io/sweep-design';
import { SingleDedupMatchingDialog } from '../routing/SingleDedupMatchingDialog';
import { AutomationType } from '../../types/enums/AutomationType';
import { AutomationsTemplatesWrapper, WrapperType } from './AutomationsTemplatesWrapper';
import { AutoTemplatesStruct } from '../../constants/automationTemplates';
import { useSweepFields } from '../../sweep-fields/useCachedSweepFields';
import { PageTemplateCanvasDialog, PageTemplates } from '../pages/pages-templates';
import { DedupTemplateContent } from '../pages/dedup-and-matching/DedupTemplateContent';
import { useAutomationsContext } from './AutomationsContext';
import useCompileTemplate from '../../hooks/useCompileTemplate';
import { DedupMatchingType } from '../../types/enums/DedupMatchingType';
import AutomationsListWithSort from './AutomationsListWithSort';
import { useExpandedMode } from '../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { ConfirmLeaveWithoutSaveDialog } from '../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { Box } from '@mui/material';
import useQueryParams from '../../navigation-utils/useQueryParams';
import { extractShortUUID } from '../../navigation-utils/readableUUID';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { SaveOrCreateAutomationParams } from './useAutomationApiPerType';
import { Filters } from '../common/filters/filters-renderer/types';

interface AutomationDialogContentProps {
  onSaveOrCreate: (params: SaveOrCreateAutomationParams) => any;
  onToggleActivation?: (
    automationJson: AutomationStructureNew,
    deployToOrgIds: string[],
    isActive: boolean,
  ) => any;
  onDeleteAutomation: (automationId: string, automationType: AutomationType) => any;
  automations: AutomationStructureNew[];
  crmOrgId: string;
  sortOptions?: AutomationSortOption[];
  disableCanvasTemplates?: boolean;
  onCreateData?: { newElement: Partial<AutomationStructureNew> };
  filterFunnelIds?: string[];
  objectType?: string;
  hideSort?: boolean;
  onDeployAutomation: (
    deployAutomationDto: AutomationStructureNew,
    deployToOrgIds: string[],
  ) => any;
  isShowWideScreenPopup?: boolean;
  filters: Filters;
  disableExpandedMode?: boolean; //used for right panel (tab in the stage dialog)
  viewInSalesforceLink?: string;
  dataTestId?: string;
}

export const AutomationDialogContent = ({
  crmOrgId,
  automations,
  onSaveOrCreate,
  onToggleActivation,
  onDeleteAutomation,
  sortOptions,
  disableCanvasTemplates,
  onCreateData,
  filterFunnelIds,
  objectType,
  hideSort,
  onDeployAutomation,
  isShowWideScreenPopup,
  filters,
  disableExpandedMode,
  viewInSalesforceLink,
  dataTestId,
}: AutomationDialogContentProps) => {
  const singleAutomationDialogRef = useRef<SingleAutomationDialogRef>(null);
  const [dedupMatchingTypeInNew, setDedupMatchingTypeInNew] = useState<DedupMatchingType>();
  const [isForceWideScreenPopup, setIsForceWideScreenPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const hasNoAutomations = useMemo(() => automations.length === 0, [automations.length]);
  const dataByVariant = useAutomationsContext();
  const { automationVariant, automationJson, setAutomationJson } = dataByVariant;
  const [isTemplateScreenOpen, setIsTemplateScreenOpen] = useState(false);
  const { getSweepFieldsById } = useSweepFields();

  const query = useQueryParams();

  const { openConfirm } = useConfirm();

  useEffect(() => {
    const shortAutomationId = extractShortUUID(query.get(DeepLinkSearchParams.automationId));
    if (shortAutomationId && automations.length > 0) {
      const automation = automations.find((automation) =>
        automation.automationId.startsWith(shortAutomationId),
      );
      if (automation) {
        setAutomationJson(automation);
        setIsEditing(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automations]);

  const closeAutomationDialog = useCallback(() => {
    setAutomationJson({});
    setDedupMatchingTypeInNew(undefined);
    setIsForceWideScreenPopup(false);
    setIsEditing(false);
  }, [setAutomationJson]);

  const _onSaveOrCreate = useCallback(
    async ({ automation, crmOrgIds, deployAs }: SaveOrCreateAutomationParams) => {
      const savedAutomation = await onSaveOrCreate({
        automation,
        crmOrgIds,
        deployAs,
      });
      if (savedAutomation?.isError) {
        return {};
      }
      closeAutomationDialog();
      return savedAutomation;
    },
    [closeAutomationDialog, onSaveOrCreate],
  );

  const openNewAutomationWithObject = useCallback(
    (objectName: string, type?: DedupMatchingType) => {
      if (type) {
        const newAutomation = getDedupMatchingDefaultAutomation(
          type ?? DedupMatchingType.LEAD_TO_LEAD_DEDUP,
        );
        setAutomationJson({
          ...newAutomation,
          objectName,
        });
        setDedupMatchingTypeInNew(type);
      } else {
        setAutomationJson({ ...onCreateData?.newElement, objectName });
      }
      setIsEditing(true);
    },
    [onCreateData?.newElement, setAutomationJson],
  );

  const compileTemplate = useCompileTemplate();

  const showRegularForm =
    automationVariant !== AutomationFormVariant.DEDUP_MATCHING &&
    automationJson?.type !== AutomationType.Dedupe &&
    automationJson?.type !== AutomationType.Matching;

  const isShowTemplatesForVariant = !disableCanvasTemplates && dataByVariant.templateSupport;
  const renderDedupTemplates = AutomationFormVariant.DEDUP_MATCHING === automationVariant;

  const getFieldsList = useCallback(
    async (fieldsList: string[]) => {
      await getSweepFieldsById({
        fieldIds: fieldsList,
      });
    },
    [getSweepFieldsById],
  );

  const changeAutomationModal = useCallback(
    (automationItem: AutomationStructureNew) => {
      setAutomationJson(automationItem);
      setIsEditing(true);
    },
    [setAutomationJson],
  );

  const onBeforeCloseModal = useCallback(
    async (automationItem: AutomationStructureNew) => {
      const isPristine = singleAutomationDialogRef.current?.isPristine();
      if (isPristine) {
        changeAutomationModal(automationItem);
      } else {
        const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
        if (isConfirmed) {
          changeAutomationModal(automationItem);
        }
      }
    },
    [changeAutomationModal, openConfirm, singleAutomationDialogRef],
  );

  const onDuplicateAutomation = useCallback(
    (automationItem: AutomationStructureNew) => {
      if (isEditing) {
        onBeforeCloseModal(automationItem);
      } else {
        setAutomationJson(automationItem);
        setIsEditing(true);
      }
    },
    [onBeforeCloseModal, setAutomationJson, isEditing],
  );

  const onEditAutomation = useCallback(
    (automationItem: AutomationStructureNew) => {
      if (isEditing) {
        onBeforeCloseModal(automationItem);
      } else {
        setAutomationJson(automationItem);
        setIsEditing(true);
      }
    },
    [onBeforeCloseModal, setAutomationJson, isEditing],
  );

  const templateButtonProps = useMemo(
    () => ({
      variant: automationVariant,
      props: {
        disabled: false,
        onSelect: (object: string) => {
          openNewAutomationWithObject(object);
          setIsTemplateScreenOpen(false);
        },
        crmOrgId,
        buttonText: 'Get started',
        automationVariant,
        isEmptyState: false,
      },
    }),
    [automationVariant, crmOrgId, openNewAutomationWithObject],
  );

  const getNewButtonWithTemplates = useCallback(() => {
    const getNewButton = () => {
      return (
        <Button
          disabled={!!isTemplateScreenOpen || isEditing}
          startIconName="Plus"
          size="small"
          onClick={() => {
            setIsTemplateScreenOpen(true);
          }}
        >
          New
        </Button>
      );
    };

    if (isShowTemplatesForVariant) {
      return (
        <>
          {getNewButton()}
          {isTemplateScreenOpen && (
            <AutomationsTemplatesWrapper
              title={`Create a new ${dataByVariant.nameInMessageSingular}`}
              onCancel={() => setIsTemplateScreenOpen(false)}
              wrapperType={WrapperType.dialog}
              automationVariant={automationVariant}
              crmOrgId={crmOrgId}
              onSelectTemplate={(item: AutoTemplatesStruct) => {
                const automationToEdit = compileTemplate(item.automation);
                setAutomationJson(automationToEdit);
                getFieldsList(item.fieldsList);
                setIsTemplateScreenOpen(false);
                setIsEditing(true);
              }}
              createEmptyButton={getAddAutomationButtonByType(templateButtonProps)}
            />
          )}
        </>
      );
    }
    if (!disableCanvasTemplates && renderDedupTemplates) {
      return (
        <>
          {getNewButton()}
          <PageTemplateCanvasDialog
            isOpen={isTemplateScreenOpen}
            onCancel={() => setIsTemplateScreenOpen(false)}
            title={`Create your next ${dataByVariant.nameInMessageSingular}`}
          >
            <DedupTemplateContent
              onSelect={(objectType, type) => {
                openNewAutomationWithObject(objectType, type);
                setIsTemplateScreenOpen(false);
              }}
            />
          </PageTemplateCanvasDialog>
        </>
      );
    }

    return getAddAutomationButtonByType({
      variant: automationVariant,
      props: {
        disabled: isEditing,
        onSelect: openNewAutomationWithObject,
        crmOrgId,
        buttonText: 'New',
        automationVariant,
        isEmptyState: false,
      },
    });
  }, [
    automationVariant,
    compileTemplate,
    crmOrgId,
    dataByVariant.nameInMessageSingular,
    disableCanvasTemplates,
    getFieldsList,
    isEditing,
    isShowTemplatesForVariant,
    isTemplateScreenOpen,
    openNewAutomationWithObject,
    renderDedupTemplates,
    setAutomationJson,
    templateButtonProps,
  ]);

  const { isExpandedMode } = useExpandedMode();
  const _isExpandedMode = isExpandedMode && !disableExpandedMode;

  const emptyStateProps = useMemo(
    () => ({
      disabled: isEditing,
      onSelect: (object: string) => {
        openNewAutomationWithObject(object);
        setIsForceWideScreenPopup(true);
      },
      crmOrgId,
      buttonText: 'Get started',
      automationVariant,
      isEmptyState: false,
    }),
    [isEditing, openNewAutomationWithObject, crmOrgId, automationVariant],
  );

  const renderEmptyAutomationsState = useCallback(() => {
    if (isShowTemplatesForVariant) {
      return (
        <AutomationsTemplatesWrapper
          wrapperType={_isExpandedMode ? WrapperType.box : WrapperType.fullPage}
          automationVariant={automationVariant}
          crmOrgId={crmOrgId}
          title={`Get started with your first ${dataByVariant.nameInMessageSingular}`}
          onSelectTemplate={(item: AutoTemplatesStruct) => {
            setAutomationJson(item.automation);
            getFieldsList(item.fieldsList);
            setIsForceWideScreenPopup(true);
            setIsEditing(true);
          }}
          createEmptyButton={getAddAutomationButtonByType({
            variant: automationVariant,
            props: emptyStateProps,
          })}
        />
      );
    } else if (renderDedupTemplates) {
      return (
        <PageTemplates
          isFullPage={true}
          title={`Create your first ${dataByVariant.nameInMessageSingular}`}
        >
          <DedupTemplateContent onSelect={openNewAutomationWithObject} />
        </PageTemplates>
      );
    } else {
      return (
        <AutomationsEmptyState
          disabled={isEditing}
          onSelect={openNewAutomationWithObject}
          crmOrgId={crmOrgId}
          title={dataByVariant.emptyStateTitle}
          subtitle={dataByVariant.emptyStateSubTitle}
          image={dataByVariant.emptyStateImage}
          defaultObjectType={objectType}
        />
      );
    }
  }, [
    isShowTemplatesForVariant,
    _isExpandedMode,
    automationVariant,
    crmOrgId,
    dataByVariant.nameInMessageSingular,
    dataByVariant.emptyStateTitle,
    dataByVariant.emptyStateSubTitle,
    dataByVariant.emptyStateImage,
    setAutomationJson,
    getFieldsList,
    renderDedupTemplates,
    openNewAutomationWithObject,
    isEditing,
    objectType,
    emptyStateProps,
  ]);

  const automationsListProps = useMemo(
    () => ({
      automations,
      crmOrgId,
      closeAutomationDialog,
      onDeployAutomation,
      onToggleActivation,
      onDeleteAutomation,
      onDuplicateAutomation,
      onEditAutomation,
      hideSort,
      getNewButtonWithTemplates,
      onCreateData,
      sortOptions,
      filters,
      isExpandedMode: !!_isExpandedMode,
      viewInSalesforceLink,
    }),
    [
      automations,
      crmOrgId,
      closeAutomationDialog,
      onDeployAutomation,
      onToggleActivation,
      onDeleteAutomation,
      onDuplicateAutomation,
      onEditAutomation,
      hideSort,
      getNewButtonWithTemplates,
      onCreateData,
      sortOptions,
      filters,
      _isExpandedMode,
      viewInSalesforceLink,
    ],
  );

  return (
    <>
      <Box
        data-testid={dataTestId}
        className="automations-dialog-content"
        sx={{
          overflow: 'hidden',
          paddingBottom: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          height: '100%',
        }}
      >
        {hasNoAutomations && renderEmptyAutomationsState()}

        {!hasNoAutomations && <AutomationsListWithSort {...automationsListProps} />}
      </Box>

      {isEditing && showRegularForm && (
        <SingleAutomationDialog
          ref={singleAutomationDialogRef}
          isOpen={isEditing}
          onSaveOrCreate={_onSaveOrCreate}
          onCancel={closeAutomationDialog}
          crmOrgId={crmOrgId}
          objectName={automationJson?.objectName ?? ''}
          filterFunnelIds={filterFunnelIds}
          isShowWideScreenPopup={isShowWideScreenPopup || isForceWideScreenPopup || _isExpandedMode}
        />
      )}
      {isEditing && !showRegularForm && (
        <SingleDedupMatchingDialog
          ref={singleAutomationDialogRef}
          isOpen={isEditing}
          newDedupMatchingType={
            dedupMatchingTypeInNew ??
            (
              automationJson?.automationDetails?.actions?.[0] as
                | DeduplicationAutomationAction
                | MatchingAutomationAction
            )?.actionParams?.dmType
          }
          onSaveOrCreate={_onSaveOrCreate}
          onCancel={closeAutomationDialog}
          crmOrgId={crmOrgId}
          objectName={automationJson.objectName ?? ''}
          forceWideScreen={hasNoAutomations || _isExpandedMode}
        />
      )}
    </>
  );
};
